import { AlertTriangle, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { DocumentStatus } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Props {
  status: DocumentStatus;
  errorMessage?: string | null;
}

export function DocumentStatusBadge({ status, errorMessage }: Props) {
  const { t } = useTranslation("knowledgeBases");
  const content = (() => {
    if (status === "pending")
      return (
        <Badge variant="outline" className="gap-1">
          <Clock className="h-3 w-3" />
          {t("document.status.pending")}
        </Badge>
      );
    if (status === "processing")
      return (
        <Badge variant="secondary" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("document.status.processing")}
        </Badge>
      );
    if (status === "indexed")
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {t("document.status.indexed")}
        </Badge>
      );
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        {t("document.status.failed")}
      </Badge>
    );
  })();

  if (status === "failed" && errorMessage) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>{errorMessage}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return content;
}
